import { inEnum, isArrayOf, isNumber, isObject } from "@ciaopeople/cp-shatsu/TypeChecking";
import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { VideoContext } from "../../player/Player.js";
import { AdBreakType, AdCallResolver, VASTTagAdUnit, enrichAdUnitWithParams, isVASTTagAdUnit } from "../AdUnit.js";
import { AdContainerControls } from "../AdsController.js";
import { AdsEnrichedVideoContext } from "../AdsEnrichedVideoContext.js";
import { ContentResumePolicy } from "../ContentResumePolicy.js";
import { UserAdsData } from "../adcall/composable/UserAdsData.js";
import { PrebidModule } from "../prebid/PrebidModule.js";
import { ImaControllerConfig } from "./AbstractImaController.js";
import { AbstractQueuedImaController } from "./AbstractQueuedImaController.js";
import { DefaultRAGPolicyMap, MultipleTagsImaEnrichedControls, ResumeAndGroupPolicyMap } from "./MultipleTagsImaEnrichedControls.js";
import { IMAUnmuter } from "../../unmuter/IMAUserGestureUnmuter.js";
import { isMediaId, MediaId } from "../../../model/MediaInfo.js";
import { GDPRSdk } from "@ciaopeople/cp-shatsu/GDPR";

/**
 * Ad unit application rule
 */
export enum AdUnitApplicationPolicy {

    /**
     * applies to all media resources
     */
    ALL_RESOURCES = 0,

    /**
     * exclude specific resources from application
     */
    EXCLUDE_RESOURCES = 1,

    /**
     * include only specific resources for application
     */
    INCLUDE_RESOURCES = 2,

    /**
     * apply only to even resources
     */
    EVEN_RESOURCES = 3,
    /**
     * apply only to odd resources
     */
    ODD_RESOURCES = 4,

    /**
     * apply only on the first resource
     */
    FIRST_MEDIA = 5,
    /**
     * applies to all resources excluding the first
     */
    ALL_EXCLUDING_FIRST = 6

} 


export enum TimeExpressionType {
    SPECIFIC_TIME = "specific-time",
    PERCENTAGE = "percentage"
}
/**
 * midroll playback time
 */
export type MidrollTime = {

    expressionType: TimeExpressionType,
    value: number

}

export function isMidrollTime(obj: any): obj is MidrollTime {
    return isObject(obj) && hasOwnProperty(obj, "expressionType") && inEnum(obj["expressionType"], TimeExpressionType) &&
    hasOwnProperty(obj, "value") && isNumber(obj["value"]);
}


export type ApplicationPolicyResourceSpec = number | MediaId;

export function isApplicationPolicyResourceSpec(obj: any): obj is ApplicationPolicyResourceSpec {
    return isNumber(obj) || isMediaId(obj);
}

/**
 * vast ad unit configuration
 */
export interface VASTAdUnitConfig {

    /**
     * unit
     */
    unit: VASTTagAdUnit,
    /**
     * application policy
     */
    applicationPolicy: AdUnitApplicationPolicy,
    /**
     * needed if `applicationPolicy` is:
     * AdUnitApplicationPolicy.EXCLUDE_RESOURCES` or
     * `AdUnitApplicationPolicy.INCLUDE_RESOURCES`
     */
    resources?: ApplicationPolicyResourceSpec[],
    /**
     * mandatory for Midroll AdUnits
     */
    midrollTime?: MidrollTime,
    /**
     * resume policy
     */
    contentResumePolicy: ContentResumePolicy
    
}

export function isVASTAdUnitConfig(obj: any) : obj is VASTAdUnitConfig {
    return isObject(obj) &&
        hasOwnProperty(obj, "unit") && isVASTTagAdUnit(obj["unit"]) &&
        hasOwnProperty(obj, "applicationPolicy") && inEnum(obj["applicationPolicy"], AdUnitApplicationPolicy) &&
        !hasOwnProperty(obj, "resources") || isArrayOf(obj["resources"], isApplicationPolicyResourceSpec) &&
        !hasOwnProperty(obj, "midrollTime") || isMidrollTime(obj["midrollTime"]) &&
        hasOwnProperty(obj, "contentResumePolicy") && inEnum(obj["contentResumePolicy"], ContentResumePolicy);

}

/**
 * preload configuration
 */
export interface AdPreloadConfig {
    /**
     * enable switch
     */
    enable: boolean,
    /**
     * amount of time before ad playback to trigger preload
     */
    time: number,

    excludePreloadOnPreroll?: boolean
}

/**
 * `MultipleVASTImaAdCallController` configuration object
 */
export interface MultipleVASTImaAdCallControllerConfig extends ImaControllerConfig {


    preload?: AdPreloadConfig
    adUnits: VASTAdUnitConfig[]
    resumeAndGroupPolicyMap?: ResumeAndGroupPolicyMap

}

/**
 * This implementation can handle multiple vast tag units for each video resource;
 * slots could be of any type: PREROLL, POSTROLL, MIDROLL and there could be multiple units
 * of the same type.
 * 
 * Units occuring at the same time will be chained
 */
export class MultipleVASTImaAdCallController extends AbstractQueuedImaController {

    
    private currentIndex: number;
    private indexMap: {[k: MediaId]: number};
    

    public constructor(ima: typeof google.ima,
        window: Window,
        gdpr: GDPRSdk,
        userAdData: UserAdsData,
        prebid: PrebidModule,
        protected config: MultipleVASTImaAdCallControllerConfig,
        adResolver: AdCallResolver,
        adContainer: AdContainerControls,
        videoElement: HTMLVideoElement,
        imaUnmuter: IMAUnmuter
        ) {

        super(ima,window,gdpr,userAdData,prebid,config,adResolver,adContainer,videoElement, imaUnmuter);
        this.currentIndex = 1;
        this.indexMap = {};
        this.config.adUnits.forEach(e => {
            e.unit = enrichAdUnitWithParams(e.unit);
        });
    }

    protected filterOut(ctx: VideoContext): VASTAdUnitConfig[] {
        
        const mediaId = ctx.ref.info.id;
        let useIdx = 1;
        if(mediaId in this.indexMap) {
            useIdx = this.indexMap[mediaId];
        }
        else {
            useIdx = this.currentIndex++;
            this.indexMap[mediaId] = useIdx;
        }
        const applicableUnits: VASTAdUnitConfig[] = [];

        if(this.config.adUnits) this.config.adUnits.forEach(ucfg => {
                const policyRes: string[] = ucfg.resources?ucfg.resources.map(el => {
                    if(isNumber(el)) return "ind-" + el;
                    else return "id-" + el;
                }):[];
                
                if(ucfg.applicationPolicy   == AdUnitApplicationPolicy.ALL_RESOURCES || 
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.ALL_EXCLUDING_FIRST && useIdx > 1) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.FIRST_MEDIA    && useIdx == 1    ) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.EVEN_RESOURCES && useIdx % 2 == 0) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.ODD_RESOURCES  && useIdx % 2 != 0) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.ODD_RESOURCES  && useIdx % 2 != 0) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.INCLUDE_RESOURCES && 
                        policyRes.findIndex((e) => e == "ind-" + useIdx || e == "id-" + mediaId) >= 0) ||
                    (ucfg.applicationPolicy == AdUnitApplicationPolicy.EXCLUDE_RESOURCES &&
                        policyRes.findIndex((e) => e == "ind-" + useIdx || e == "id-" + mediaId) < 0)
                ) {
                        if(ucfg.unit.breakType == AdBreakType.MIDROLL) {
                            const midrollTime: MidrollTime = ucfg.midrollTime?ucfg.midrollTime:{
                                expressionType: TimeExpressionType.PERCENTAGE,
                                value: 50
                            };
                            if(midrollTime.value > 0 && (
                                (midrollTime.expressionType == TimeExpressionType.PERCENTAGE && midrollTime.value < 100) ||
                                (midrollTime.expressionType == TimeExpressionType.SPECIFIC_TIME))
                            ){

                                applicableUnits.push(ucfg);    

                            }               

                        }
                        else applicableUnits.push(ucfg);
                }

        })

        return applicableUnits;

    }

    buildEnrichedVideoContext(ctx: VideoContext, willAutoplay: boolean): Promise<AdsEnrichedVideoContext> {
        const useUnits: VASTAdUnitConfig[] = this.filterOut(ctx);

        return Promise.resolve(new MultipleTagsImaEnrichedControls(ctx,
            this.adContainerControls,this,this.config.preload?{
                ...this.config.preload,
                excludePreloadOnPreroll: !willAutoplay
            }:{
            enable: false,
            time: 0,
            excludePreloadOnPreroll: !willAutoplay
        },useUnits,true,this.config.adResumableTriggerBehaviour,this.config.resumeAndGroupPolicyMap?this.config.resumeAndGroupPolicyMap:DefaultRAGPolicyMap))
    }

}
